/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { WarningFilled } from '@ant-design/icons';
import { Input as AntDInput, Modal, Space } from 'antd';

import { CreateInstallerTokenResponse } from '@/client';
import { useMessage } from '@/hooks/message';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { Button } from '@/primitives/Button';
import { Container } from '@/primitives/Container';
import { CopyButton } from '@/primitives/CopyButton';
import { Text } from '@/primitives/Text';

type InstallerTokenCreatedModalProps = {
  createdToken: CreateInstallerTokenResponse;
  onClose: () => void;
};

export function InstallerTokenCreatedModal({ createdToken, onClose }: InstallerTokenCreatedModalProps) {
  const { t } = useMimicTranslation('installerTokens');
  const [message] = useMessage();

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(createdToken.data.token);
      message.success(t('tokenGeneratedAndCopied'));
    } catch (error) {
      message.error(t('failedCopy'));
    }
  };

  const handleCopyAndClose = async () => {
    await copyToClipboard();
    onClose();
  };

  return (
    <Modal
      open
      title={t('successModalTitle')}
      onCancel={onClose}
      footer={[
        <Button key="copy-close" type="primary" onClick={handleCopyAndClose} dataTestId="copy-and-close-button">
          {t('copyAndClose')}
        </Button>,
      ]}
      width={600}
      centered
    >
      <Container>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%', marginTop: '1rem' }}>
          <Text>{t('successModalDescription')}</Text>

          <div>
            <div style={{ marginBottom: '0.5rem' }}>
              <Text>{t('tokenIdLabel')}</Text>
            </div>
            <Space.Compact style={{ width: '100%' }}>
              <AntDInput readOnly value={createdToken.data.token} data-testid="token-field" />
              <CopyButton textToCopy={createdToken.data.token} failedText={t('failedCopy')} />
            </Space.Compact>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.5rem',
            }}
          >
            <WarningFilled style={{ color: 'rgb(222, 75, 28)', marginTop: '0.1rem' }} />
            <Text size="md" italic style={{ flex: 1, margin: 0 }}>
              {t('tokenNotVisibleWarning')}
            </Text>
          </div>
        </div>
      </Container>
    </Modal>
  );
}
